import React, { useCallback } from 'react';
import { SecondaryNavContainer } from '../../../components/SecondaryNav';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import SchemaModelNav from './components/SchemaModelNav';
import {
  dataModelerCreateDraftSchema,
  dataModelerSetCurrentSchemaId,
} from '../../../redux/slices/dataModeler';
import { batch } from 'react-redux';
import { dataModelerSelectCurrentSchemaObj } from '../../../redux/selectors/dataModeler';
import {
  autoSchemaKeyFromName,
  useDataModelerPermittedSchemas,
} from '../DataModelerSchema/utils';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../../../path';
import CreateNewSchemaButton from './components/CreateNewSchemaButton';
import { PermissionKey } from '../../../utils/apollo/graphqlSchema';
import { useDataModelerEditFeature } from '../../../utils/hooks';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { dataModelLabelInfoTooltipOptions } from '../../../components/KubitTooltip/globalTooltips';
import useCustomerPlan from '../../../utils/hooks/useCustomerPlan';

const DataModelerNav = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const permissionConfig = useBootstrapSelector('permissionConfig');

  const { checkIsPermitted } = usePermission();

  const { currentSchemaId, permittedSchemas } =
    useDataModelerPermittedSchemas();
  const { maxSchemas, hasAvailableSchemas } = useCustomerPlan();

  const currentSchemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  const isPermittedToCreateNewSchema = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.DataModelView,
      PermissionKey.DataModelEdit,
      PermissionKey.DataModelCreate,
    ],
    schemaId: permissionConfig?.allSchema?.id,
  });

  const dataModelerEditFeatureEnabled = useDataModelerEditFeature();
  const isEditMode =
    isPermittedToCreateNewSchema && dataModelerEditFeatureEnabled;

  const handleSelectItem = useCallback(
    (schemaId: string) => {
      navigate(appRoutes.modeler.modeler(schemaId));
    },
    [navigate],
  );

  const handleCreateSchema = () => {
    if (!isEditMode || !hasAvailableSchemas) {
      return;
    }
    const knownSchemaNames = permittedSchemas.map(
      (permittedSchema) => permittedSchema.name,
    );
    const knownSchemaKeys = permittedSchemas.map(
      (permittedSchema) => permittedSchema.id,
    );
    const desiredSchemaName = 'New Schema';
    let uniqueName = desiredSchemaName;
    let isDuplicate = knownSchemaNames.includes(uniqueName);
    let n = 0;
    while (isDuplicate) {
      n += 1;
      uniqueName = `${desiredSchemaName} (${n})`;
      isDuplicate = knownSchemaNames.includes(uniqueName);
    }
    const uniqueKey = autoSchemaKeyFromName(uniqueName, knownSchemaKeys);
    batch(() => {
      dispatch(
        dataModelerCreateDraftSchema({
          schemaKey: uniqueKey,
          schemaName: uniqueName,
          templateDataSource: currentSchemaObj?.dsConfig,
          templateDataRefresh: currentSchemaObj?.dataRefresh ?? null,
        }),
      );
      dispatch(dataModelerSetCurrentSchemaId({ schemaId: uniqueKey }));
    });
    navigate(appRoutes.modeler.modeler(uniqueKey));
  };

  const warning = maxSchemas
    ? `${maxSchemas.usedSchemas} of  ${maxSchemas.maxSchemas} Schemas created`
    : '';

  const newSchemaTooltip = !hasAvailableSchemas
    ? dataModelLabelInfoTooltipOptions.maxSchemasReached.content(
        maxSchemas?.maxSchemas,
      )
    : 'New Schema';

  return (
    <SecondaryNavContainer
      heading={'Data Modeler'}
      headingIsBeta={true}
      warning={warning}
    >
      {isEditMode && (
        <CreateNewSchemaButton
          tooltip={newSchemaTooltip}
          onClick={handleCreateSchema}
          disabled={!hasAvailableSchemas}
        />
      )}
      {permittedSchemas.map((schema) => (
        <SchemaModelNav
          key={`schema-nav-${schema.id}`}
          schema={schema}
          isSelected={schema.id === currentSchemaId}
          onSelectItem={handleSelectItem}
        />
      ))}
    </SecondaryNavContainer>
  );
};

export default DataModelerNav;
